import { useEffect, useMemo, useState } from "react";
import {
    Activity,
    Clock,
    DatabaseZap,
    PauseCircle,
    PlayCircle,
    RefreshCw,
    RotateCcw,
    Save,
    Settings2,
    ShieldCheck,
    Zap
} from "lucide-react";
import toast from "react-hot-toast";
import {
    getAdminJobsConfig,
    saveAdminJobsConfig,
    getAdminJobStatuses,
    setAdminJobEnabled,
    getAdminJobRunLogs,
    runSystemMaintenanceNow,
    runWebsiteRecoveryNow,
    processNextProjectModuleSync,
    markStaleProjectModuleSyncRuns,
    processNextWebsiteRedeploy
} from "../services/crmService";
import "./AdminJobsPage.css";
import PageShell from "../components/layout/PageShell";

const jobGroups = [
    {
        key: "project-module-sync",
        title: "Project Module Sync",
        icon: DatabaseZap,
        enabledKey: "projectModuleSyncEnabled",
        description: "CRM module bootstrap, seed sync, stale sync detection, and redeploy queue handoff.",
        fields: [
            ["projectModuleSyncIdleSeconds", "Idle seconds"],
            ["projectModuleSyncHotLoopMilliseconds", "Hot loop ms"],
            ["projectModuleSyncErrorBackoffSeconds", "Error backoff seconds"],
            ["projectModuleSyncStaleMinutes", "Stale minutes"],
            ["projectModuleSyncStaleCheckSeconds", "Stale check seconds"]
        ],
        actions: ["module-sync", "module-sync-stale"]
    },
    {
        key: "website-redeploy",
        title: "Website Redeploy",
        icon: Zap,
        enabledKey: "websiteRedeployEnabled",
        description: "Processes CRM-triggered website redeploy runs and pushes updated generated sites.",
        fields: [
            ["websiteRedeployIdleSeconds", "Idle seconds"],
            ["websiteRedeployHotLoopMilliseconds", "Hot loop ms"],
            ["websiteRedeployErrorBackoffSeconds", "Error backoff seconds"]
        ],
        actions: ["redeploy"]
    },
    {
        key: "website-recovery",
        title: "Website Recovery",
        icon: RotateCcw,
        enabledKey: "websiteRecoveryEnabled",
        description: "Scans pending, retryable, and stuck generation projects and safely requeues them.",
        fields: [
            ["websiteRecoveryIntervalSeconds", "Interval seconds"],
            ["websiteRecoveryErrorBackoffSeconds", "Error backoff seconds"],
            ["websiteRecoveryStuckMinutes", "Stuck minutes"]
        ],
        actions: ["recovery"]
    },
    {
        key: "system-maintenance",
        title: "System Maintenance",
        icon: ShieldCheck,
        enabledKey: "systemMaintenanceEnabled",
        description: "Cleans request logs, SQL logs, old preview sessions, and stale recovery data.",
        fields: [
            ["systemMaintenanceStartupDelayMinutes", "Startup delay minutes"],
            ["systemMaintenanceIntervalHours", "Interval hours"],
            ["requestLogRetentionDays", "Request log retention days"],
            ["previewSessionExpireAfterDays", "Preview expire after days"],
            ["previewSessionDeleteAfterDays", "Preview delete after days"]
        ],
        actions: ["maintenance"]
    }
];

export default function AdminJobsPage() {
    const [timing, setTiming] = useState(null);
    const [statuses, setStatuses] = useState([]);
    const [runLogs, setRunLogs] = useState([]);
    const [selectedJobKey, setSelectedJobKey] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [running, setRunning] = useState("");

    const enabledCount = useMemo(() => {
        if (!timing) return 0;

        return jobGroups.filter((job) => timing[job.enabledKey] !== false).length;
    }, [timing]);

    const latestRun = runLogs?.[0];

    const load = async () => {
        setLoading(true);

        try {
            const [configResult, statusesResult, logsResult] = await Promise.all([
                getAdminJobsConfig(),
                getAdminJobStatuses(),
                getAdminJobRunLogs(selectedJobKey, 40)
            ]);

            setTiming(configResult?.timing || {});
            setStatuses(statusesResult?.items || []);
            setRunLogs(logsResult?.items || []);
        } catch (error) {
            console.error(error);
            toast.error("Could not load System Jobs.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedJobKey]);

    const updateNumber = (key, value) => {
        setTiming((current) => ({
            ...(current || {}),
            [key]: Number(value)
        }));
    };

    const updateBool = (key, value) => {
        setTiming((current) => ({
            ...(current || {}),
            [key]: value
        }));
    };

    const save = async () => {
        if (!timing) return;

        try {
            setSaving(true);

            const result = await saveAdminJobsConfig(timing);

            setTiming(result?.timing || timing);
            toast.success("System job configuration saved.");

            await load();
        } catch (error) {
            console.error(error);

            const message =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                "Could not save job configuration.";

            toast.error(message);
        } finally {
            setSaving(false);
        }
    };

    const toggleJob = async (job) => {
        if (!timing) return;

        const nextValue = timing[job.enabledKey] === false;

        try {
            updateBool(job.enabledKey, nextValue);

            await setAdminJobEnabled(job.key, nextValue);

            toast.success(`${job.title} ${nextValue ? "enabled" : "paused"}.`);

            await load();
        } catch (error) {
            console.error(error);
            updateBool(job.enabledKey, !nextValue);
            toast.error("Could not update job status.");
        }
    };

    const runAction = async (actionKey) => {
        try {
            setRunning(actionKey);

            let result;

            if (actionKey === "maintenance") {
                result = await runSystemMaintenanceNow();
            } else if (actionKey === "recovery") {
                result = await runWebsiteRecoveryNow();
            } else if (actionKey === "module-sync") {
                result = await processNextProjectModuleSync();
            } else if (actionKey === "module-sync-stale") {
                result = await markStaleProjectModuleSyncRuns();
            } else if (actionKey === "redeploy") {
                result = await processNextWebsiteRedeploy();
            }

            if (result?.success === false) {
                toast.error(result?.message || "Job failed.");
            } else {
                toast.success(result?.message || "Job completed.");
            }

            await load();
        } catch (error) {
            console.error(error);

            const message =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                "Job action failed.";

            toast.error(message);
        } finally {
            setRunning("");
        }
    };

    return (
        <PageShell title="SystemJogs">
            <div className="jobs-page">
                <section className="jobs-hero">
                    <div>
                        <span className="jobs-kicker">SuperAdmin controls</span>
                        <h1>System Jobs</h1>
                        <p>
                            Configure platform workers, pause jobs, trigger safe manual actions,
                            and review recent job run history without redeploying code.
                        </p>
                    </div>

                    <div className="jobs-hero-actions">
                        <button type="button" onClick={load} disabled={loading}>
                            <RefreshCw size={16} />
                            Refresh
                        </button>

                        <button
                            type="button"
                            className="primary"
                            onClick={save}
                            disabled={saving || loading || !timing}
                        >
                            <Save size={16} />
                            {saving ? "Saving..." : "Save Config"}
                        </button>
                    </div>
                </section>

                <section className="jobs-summary-grid">
                    <SummaryCard
                        icon={Settings2}
                        label="Configured jobs"
                        value={jobGroups.length}
                        hint="Workers available"
                    />
                    <SummaryCard
                        icon={Activity}
                        label="Enabled"
                        value={enabledCount}
                        hint="Currently active"
                    />
                    <SummaryCard
                        icon={Clock}
                        label="Run logs"
                        value={runLogs.length}
                        hint="Loaded history"
                    />
                    <SummaryCard
                        icon={PlayCircle}
                        label="Latest run"
                        value={latestRun?.status || "-"}
                        hint={latestRun?.jobName || "No run yet"}
                    />
                </section>

                {loading ? (
                    <section className="jobs-empty">Loading job configuration...</section>
                ) : null}

                {!loading && timing ? (
                    <section className="jobs-grid">
                        {jobGroups.map((group) => {
                            const Icon = group.icon;
                            const isEnabled = timing[group.enabledKey] !== false;
                            const status = statuses.find((x) => x.jobKey === group.key);

                            return (
                                <article
                                    key={group.key}
                                    className={`jobs-card ${isEnabled ? "" : "paused"}`}
                                >
                                    <div className="jobs-card-head">
                                        <div className="jobs-card-icon">
                                            <Icon size={18} />
                                        </div>

                                        <div>
                                            <strong>{group.title}</strong>
                                            <p>{group.description}</p>
                                        </div>
                                    </div>

                                    <div className="jobs-status-row">
                                        <button
                                            type="button"
                                            className={`jobs-toggle-pill ${isEnabled ? "enabled" : "paused"}`}
                                            onClick={() => toggleJob(group)}
                                        >
                                            {isEnabled ? <PlayCircle size={15} /> : <PauseCircle size={15} />}
                                            {isEnabled ? "Enabled" : "Paused"}
                                        </button>

                                        {status?.canRunManually ? (
                                            <span className="jobs-manual-pill">Manual trigger available</span>
                                        ) : (
                                            <span className="jobs-manual-pill muted">Queue only</span>
                                        )}
                                    </div>

                                    <div className="jobs-fields">
                                        {group.fields.map(([key, label]) => (
                                            <label key={key}>
                                                <span>{label}</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={timing[key] ?? 0}
                                                    onChange={(e) => updateNumber(key, e.target.value)}
                                                />
                                            </label>
                                        ))}
                                    </div>

                                    <div className="jobs-actions-row">
                                        {group.actions?.includes("module-sync") ? (
                                            <button
                                                type="button"
                                                className="jobs-run-btn"
                                                onClick={() => runAction("module-sync")}
                                                disabled={running === "module-sync"}
                                            >
                                                <PlayCircle size={16} />
                                                {running === "module-sync" ? "Running..." : "Process next sync"}
                                            </button>
                                        ) : null}

                                        {group.actions?.includes("module-sync-stale") ? (
                                            <button
                                                type="button"
                                                className="jobs-run-btn secondary"
                                                onClick={() => runAction("module-sync-stale")}
                                                disabled={running === "module-sync-stale"}
                                            >
                                                <PlayCircle size={16} />
                                                {running === "module-sync-stale" ? "Checking..." : "Mark stale syncs"}
                                            </button>
                                        ) : null}

                                        {group.actions?.includes("redeploy") ? (
                                            <button
                                                type="button"
                                                className="jobs-run-btn"
                                                onClick={() => runAction("redeploy")}
                                                disabled={running === "redeploy"}
                                            >
                                                <PlayCircle size={16} />
                                                {running === "redeploy" ? "Running..." : "Process next redeploy"}
                                            </button>
                                        ) : null}

                                        {group.actions?.includes("recovery") ? (
                                            <button
                                                type="button"
                                                className="jobs-run-btn"
                                                onClick={() => runAction("recovery")}
                                                disabled={running === "recovery"}
                                            >
                                                <PlayCircle size={16} />
                                                {running === "recovery" ? "Running..." : "Run recovery now"}
                                            </button>
                                        ) : null}

                                        {group.actions?.includes("maintenance") ? (
                                            <button
                                                type="button"
                                                className="jobs-run-btn"
                                                onClick={() => runAction("maintenance")}
                                                disabled={running === "maintenance"}
                                            >
                                                <PlayCircle size={16} />
                                                {running === "maintenance" ? "Running..." : "Run cleanup now"}
                                            </button>
                                        ) : null}
                                    </div>
                                </article>
                            );
                        })}
                    </section>
                ) : null}

                <section className="jobs-history-card">
                    <div className="jobs-history-head">
                        <div>
                            <strong>Recent job runs</strong>
                            <span>Manual trigger audit trail</span>
                        </div>

                        <select
                            value={selectedJobKey}
                            onChange={(e) => setSelectedJobKey(e.target.value)}
                        >
                            <option value="">All jobs</option>
                            {jobGroups.map((job) => (
                                <option key={job.key} value={job.key}>
                                    {job.title}
                                </option>
                            ))}
                            <option value="project-module-sync-mark-stale">Project Module Sync - Mark Stale</option>
                        </select>
                    </div>

                    <div className="jobs-history-list">
                        {runLogs.map((log) => (
                            <article key={log.id} className="jobs-history-item">
                                <div>
                                    <strong>{log.jobName}</strong>
                                    <span>{log.jobKey}</span>
                                </div>

                                <em className={String(log.status || "").toLowerCase()}>
                                    {log.status}
                                </em>

                                <small>
                                    {formatDate(log.startedAtUtc)}
                                    {log.durationMs ? ` • ${log.durationMs}ms` : ""}
                                </small>

                                {log.triggeredByEmail ? (
                                    <p>Triggered by {log.triggeredByEmail}</p>
                                ) : null}

                                {log.summary ? <p>{log.summary}</p> : null}

                                {log.errorMessage ? (
                                    <p className="error">{log.errorMessage}</p>
                                ) : null}

                                {log.dataJson ? (
                                    <details>
                                        <summary>View result JSON</summary>
                                        <pre>{formatJson(log.dataJson)}</pre>
                                    </details>
                                ) : null}
                            </article>
                        ))}

                        {runLogs.length === 0 ? (
                            <p className="jobs-empty small">No job runs found.</p>
                        ) : null}
                    </div>
                </section>
            </div>
        </PageShell>
    );
}

function SummaryCard({ icon: Icon, label, value, hint }) {
    return (
        <article className="jobs-summary-card">
            <span>
                <Icon size={18} />
            </span>
            <strong>{value}</strong>
            <small>{label}</small>
            <em>{hint}</em>
        </article>
    );
}

function formatDate(value) {
    if (!value) return "-";

    try {
        return new Date(value).toLocaleString();
    } catch {
        return value;
    }
}

function formatJson(value) {
    try {
        return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
        return value;
    }
}